/**
 * useCustomPricing Hook
 *
 * Fetches per-date pricing overrides from listings/{id}/customPricing for a
 * stay window [checkIn, checkOut). Doc IDs are YYYY-MM-DD, so a documentId()
 * range query needs no composite index. Returns null while loading so callers
 * can hold off computing totals until overrides are known.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { formatDate } from '../../../utils/dates';

export interface CustomPricingEntry {
  price?: number;
  minNights?: number;
}

export type CustomPricingMap = Map<string, CustomPricingEntry>;

export function useCustomPricing(
  listingId: string | undefined,
  checkIn: Date | null,
  checkOut: Date | null
): CustomPricingMap | null {
  const [map, setMap] = useState<CustomPricingMap | null>(null);

  useEffect(() => {
    if (!listingId || !checkIn || !checkOut) {
      setMap(new Map());
      return;
    }

    let cancelled = false;
    setMap(null);

    const startKey = formatDate(checkIn, 'numeric');
    // Checkout day is not a night; exclusive end via '<'.
    const endKey = formatDate(checkOut, 'numeric');

    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'listings', listingId, 'customPricing'),
            where(documentId(), '>=', startKey),
            where(documentId(), '<', endKey)
          )
        );
        if (cancelled) return;
        const result: CustomPricingMap = new Map();
        snap.forEach((d) => {
          const data = d.data();
          result.set(d.id, { price: data.price, minNights: data.minNights });
        });
        setMap(result);
      } catch (err) {
        console.error('Failed to fetch custom pricing:', err);
        if (!cancelled) setMap(new Map());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [listingId, checkIn?.getTime(), checkOut?.getTime()]);

  return map;
}

export default useCustomPricing;
